// ToolActivityView — the agent's TOOL-mechanics progress, promoted from the
// Storybook sketch at `sketches/tool-activity/` (dogfood F18) into the real
// chat surface. Replaces `ChatMessage`'s old flat `AgentActivityTrail` render:
// a collapsed one-line quiet summary that does NOT disturb the main chat flow
// ("Read the source and 2 more tools ›"); expanded, GROUPS consecutive similar
// calls under one skill-call header instead of one equally-weighted row per
// call. `selectToolActivity.ts` is the pure selector that turns the live
// `AgentActivityStep[]` stream into the `ToolActivityGroup[]` this component
// wants — this file stays a pure props-driven projection and does not change
// when that mapping does.
//
// State: `open` (whole trail collapsed/expanded) and `openGroups` (which
// multi-item groups are expanded) are BOTH view-ephemeral UI state — they
// survive no reload and carry no domain meaning — same posture as the old
// `AgentActivityTrail`'s own `open` flag. Everything else arrives via props.
//
// alarm-discipline: ambient tool mechanics stay quiet and tucked away by
// default (closed disclosure); a failed call is marked by an icon PLUS a
// tone change, never colour alone. No borders — hierarchy comes from
// spacing/indentation and the chevron, never a painted edge. No `as` casts.
//
// TENSE: a verb is a `VerbLabel` (present + past form); a group/item reads in
// present tense while any of its calls is still running, past tense once
// every call has settled, so "Searching the web" resolves to "Searched the
// web" instead of staying present-tense forever.
//
// LINKS: an item/result whose `onOpen` is present names a referenced entity
// (a source, a file, a search hit) and renders as a clickable link, never
// plain text. Since F124 the live selector fills `resources` (the call's
// typed detail, as composer @-mention chips that open in the right panel)
// and `onDownload` for an exported package; see `selectToolActivity.ts`.
//
// FALLBACK: a child row with neither its own verb, summary, nor result (e.g.
// one probe inside a "Checked the reading level" group) always renders a
// muted "No details" line, so an expanded group is never a blank row.
//
// PULSE: the running spark's stray `margin-top` (misaligning it against both
// the centred collapsed header and its sibling status glyphs) moved to the
// one context that actually needs it (see ToolActivityView.css).

export type {
    ToolActivityGroup,
    ToolActivityViewModel,
    ToolCallItem,
    ToolCallReadRange,
    ToolCallReadSpan,
    ToolCallResource,
    ToolCallStatus,
};
export {
    collapsedSummary,
    rangeLabel,
    readSpanPrefix,
    rowVerbShown,
    ToolActivitySteps,
    ToolActivityView,
    trailGlyph,
};

import { exhaustSwitchCase } from "@bilda/utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Button } from "../../Button/Button.js";
import { ResourceMentionChip } from "../../Composer/PowerComposer/ResourceMentionChip.js";
import type { ComposerResourceKind } from "../../Composer/PowerComposer/types.js";
import { Icon } from "../../Icon/Icon.js";
import { Link } from "../../Link/Link.js";
import { Text } from "../../Text/Text.js";
import { type TrailGlyph, TrailLine } from "../TrailLine/TrailLine.js";
import "./ToolActivityView.css";

// === View-model — the read-contract this component projects from ===========

type ToolCallStatus = "running" | "done" | "failed";

// A verb phrase in BOTH tenses, so a group/item can read "Searching the
// web" while in flight and settle to "Searched the web" once it's done —
// never stuck in one tense regardless of status (dogfood F18).
type VerbLabel = {
    readonly present: string; // in-progress phrasing — "Searching the web"
    readonly past: string; // settled phrasing — "Searched the web"
    // Present ⇒ `past` names one object ("Read the source"); this says how
    // many distinct ones ("Read 2 sources") — G10.
    readonly pastCounted?: (count: number) => string;
};

// What a single call resolved to, when that's worth a second line — a
// fetched page's title + domain, a search hit's title + domain, etc.
// Absent ⇒ the call speaks for itself (its summary line is enough).
type ToolCallResult = {
    readonly title: string;
    readonly meta?: string; // domain / path / kind — shown faint after title
    // Present ⇒ this result NAMES a referenced entity (a search hit, a
    // fetched page) with somewhere to go — render `title` as a clickable
    // link. Absent ⇒ plain text (dogfood F18: referenced entities must be
    // links, not inert strings).
    readonly onOpen?: () => void;
};

type ToolCallItem = {
    readonly id: string;
    // The relevant PARAMETER as a short human string — a query, a filename,
    // a URL — never the raw tool-call JSON. Absent ⇒ the verb alone is the
    // whole sentence (some calls, e.g. "Checked the reading level", take no
    // parameter worth surfacing).
    readonly summary?: string;
    readonly status: ToolCallStatus;
    readonly result?: ToolCallResult;
    // Present ⇒ `summary` itself NAMES a referenced entity this call acted
    // on (a source/file, e.g. "Cellbiologi – kapitel 3.pdf") — render it as
    // a clickable link. Absent ⇒ `summary` is a query/parameter string with
    // no navigable target (e.g. a search query) and stays plain text.
    readonly onOpen?: () => void;
    // Only meaningful when `status` is "failed" — a short, honest reason
    // (never "try again" baked in).
    readonly errorReason?: string;
    // What the call found, as a short second sentence ("Hittade 6 källor");
    // the resources then sit on that line instead of the intent line.
    readonly resultLine?: string;
    // The resources this call read, changed, cited, exported or listed
    // (F124), as chips; a long list shows the first few behind a "+N more".
    readonly resources?: readonly ToolCallResource[];
    // Present ⇒ the call produced a downloadable package (F124).
    readonly onDownload?: () => void;
    // Present ⇒ the call failed because a source's stored content is missing;
    // re-uploads the original file INTO that same source (the recovery).
    readonly onReupload?: () => void;
    // Present ⇒ WHICH parts of its one resource the call(s) read ("s. 12–15,
    // 80 i [källa]"); consecutive reads of the same source merge into one.
    readonly readSpan?: ToolCallReadSpan;
};

// The parts read, as sorted, merged inclusive ranges; each range a link that
// opens the source there (when an opener is wired).
type ToolCallReadRange = {
    readonly first: number;
    readonly last: number;
    readonly onOpen?: () => void;
};
type ToolCallReadSpan = {
    readonly unit: "pages" | "sections";
    readonly ranges: readonly [ToolCallReadRange, ...ToolCallReadRange[]];
};

// A resource a call named. `openable` renders as the composer's @-mention
// chip, clickable; `inert` (a raw file, a deleted resource, or no opener
// wired) is its title as plain text.
type ToolCallResource =
    | {
          readonly type: "openable";
          readonly id: string;
          readonly title: string;
          readonly kind: ComposerResourceKind;
          readonly onOpen: () => void;
      }
    | {
          readonly type: "inert";
          readonly id: string;
          readonly title: string;
      };

// A GROUP is one skill-call header shared by 1+ consecutive similar calls.
// Its verb phrase never pluralises by count (repeated web searches still
// read as "Searched the web", not "searched the web 3 times" — the
// reference behaviour this component is chasing).
type ToolActivityGroup = {
    readonly id: string;
    readonly verb: VerbLabel; // { present: "Searching the web", past: "Searched the web" }
    readonly items: readonly [ToolCallItem, ...ToolCallItem[]];
};

type ToolActivityViewModel = {
    // Chronological order; a group of size 1 renders as a plain row (no
    // header/chevron of its own) — grouping is a fact about the DATA, not an
    // extra affordance this component invents.
    readonly groups: readonly ToolActivityGroup[];
};

type ToolActivityViewProps = {
    readonly model: ToolActivityViewModel;
};

// === Collapsed one-liner — pure derivation over the groups =================

const lowerFirst = (s: string): string =>
    s.length === 0 ? s : s[0].toLowerCase() + s.slice(1);

type Tense = "present" | "past";

// A single call reads present tense while it's still running, past tense
// once it's settled — done or failed alike (a failed call already happened).
const itemTense = (status: ToolCallStatus): Tense =>
    status === "running" ? "present" : "past";

// A group reads present tense while ANY of its calls is still running, past
// tense once every call has settled — so "Searching the web" resolves to
// "Searched the web" as soon as the last search comes back.
const groupTense = (group: ToolActivityGroup): Tense =>
    group.items.some((item) => item.status === "running") ? "present" : "past";

const verbForm = (verb: VerbLabel, tense: Tense): string =>
    tense === "present" ? verb.present : verb.past;

// How many distinct resources a group's calls named (reads of one source
// collapse to one item, but separate groups of the same tool merge in the
// summary, so count ids, not items).
const distinctResources = (group: ToolActivityGroup): number =>
    new Set(
        group.items.flatMap((item) => (item.resources ?? []).map((r) => r.id)),
    ).size;

// A group's verb in a tense, counted when it names several distinct objects
// ("Read 2 sources", not "Read the source") — G10.
const groupVerb = (group: ToolActivityGroup, tense: Tense): string => {
    const count = distinctResources(group);
    return tense === "past" && group.verb.pastCounted !== undefined && count > 1
        ? group.verb.pastCounted(count)
        : verbForm(group.verb, tense);
};

// The clause for one group: "Read the material". `leading` picks the
// sentence-initial capitalised form vs the mid-sentence lowered one. (A
// hand-pluralised English "read 4 files" count was dropped: it could not be
// localized, F122.) TENSE (F122): the
// collapsed summary describes a SETTLED trail — what is happening now is the
// chat's live row (D51, DraftingIndicator) — so every clause reads PAST
// tense, never the mixed "Läser …, paketerade …" a per-group `groupTense`
// produced when one group had stopped and another was still counted running.
const groupClause = (group: ToolActivityGroup, leading: boolean): string => {
    const verbText = groupVerb(group, "past");
    return leading ? verbText : lowerFirst(verbText);
};

// The collapsed one-liner names KINDS of work, not every call. Two "Read the
// material" groups separated by a "Packaged the material" group must read as
// one "Läste materialet", not the same clause twice (F122: near-identical
// calls were listed verbatim). Dedupe by the settled verb — its identifying
// label — merging items so a count-bearing clause still totals correctly;
// first-occurrence order is kept. The EXPANDED trail keeps every chronological
// group untouched — this fold is for the one-line summary only.
const dedupeByKind = (
    groups: readonly ToolActivityGroup[],
): readonly ToolActivityGroup[] => {
    const byKind = new Map<string, ToolActivityGroup>();
    for (const group of groups) {
        const existing = byKind.get(group.verb.past);
        byKind.set(
            group.verb.past,
            existing === undefined
                ? group
                : { ...existing, items: [...existing.items, ...group.items] },
        );
    }
    return [...byKind.values()];
};

// "Searched the web, read 4 files, and 5 more tools" — names the first two
// KINDS of work, folds the rest into a count. The connectors ("and", "N more
// tools") are localized via Lingui (F122: they were hardcoded English while
// the verb clauses were Swedish, so the line mixed languages); the clauses
// are already-localized verb phrases handed in as values. Returns a plain
// string when there is no connector to localize (0 or 1 kind), a
// `MessageDescriptor` otherwise — the component resolves it with `i18n._`.
const collapsedSummary = (
    groups: readonly ToolActivityGroup[],
): string | MessageDescriptor => {
    const kinds = dedupeByKind(groups);
    const first = kinds.at(0);
    if (first === undefined) return "";
    const second = kinds.at(1);
    if (second === undefined) return groupClause(first, true);
    const firstClause = groupClause(first, true);
    const secondClause = groupClause(second, false);
    if (kinds.length === 2) {
        return msg`${firstClause} and ${secondClause}`;
    }
    // Three kinds: name the third rather than hide one step behind "and 1
    // more" (G10) — "1 more" names nothing and costs as much to read.
    const third = kinds.at(2);
    if (kinds.length === 3 && third !== undefined) {
        const thirdClause = groupClause(third, false);
        return msg`${firstClause}, ${secondClause} and ${thirdClause}`;
    }
    const restCount = kinds.length - 2;
    return msg`${firstClause}, ${secondClause}, and ${plural(restCount, {
        one: "# more tool",
        other: "# more tools",
    })}`;
};

// A lone row under a lone group IS the collapsed header's own sentence, so
// its expanded line leaves the verb out and shows only what it ADDS (its
// parameter, result, chips, failure reason); the status glyph keeps the
// outcome visible (D17). With several groups the verb tells the rows apart.
const rowVerbShown = (groups: readonly ToolActivityGroup[]): boolean =>
    groups.length !== 1 || groups[0].items.length !== 1;

// === Component ===============================================================

const StatusGlyph = ({
    status,
}: {
    readonly status: ToolCallStatus;
}): ReactNode => {
    switch (status) {
        case "running":
            return <span className="tav-spark" aria-hidden="true" />;
        case "done":
            return <Icon name="check" size="xs" className="tav-glyph--done" />;
        case "failed":
            return (
                <Icon name="close" size="xs" className="tav-glyph--failed" />
            );
        default:
            return exhaustSwitchCase(status);
    }
};

// A referenced entity (a source, a file, a search hit) renders as a link
// when the caller hands us somewhere to send the click; otherwise it's the
// plain text it always was (dogfood F18: entities must be clickable, but a
// bare query string with no target stays inert).
const EntityText = ({
    text,
    onOpen,
}: {
    readonly text: string;
    readonly onOpen?: () => void;
}): ReactNode =>
    onOpen === undefined ? (
        text
    ) : (
        <Link
            tone="neutral"
            href="#"
            className="tav-link"
            onClick={(event) => {
                event.preventDefault();
                onOpen();
            }}
        >
            {text}
        </Link>
    );

// The row's primary sentence: "<Verb>: <entity>" for a solo row, the bare
// verb when there's no parameter worth naming, the entity alone for a
// child row (the group header already said the verb) — and, when a child
// row has neither a verb nor its own summary (e.g. one probe inside a
// "Checked the reading level" group), a guaranteed muted fallback so an
// expanded group never renders a blank row (dogfood F18).
const primaryLine = ({
    verb,
    item,
}: {
    readonly verb?: string;
    readonly item: ToolCallItem;
}): ReactNode => {
    if (verb !== undefined) {
        return item.summary !== undefined ? (
            <>
                {verb}: <EntityText text={item.summary} onOpen={item.onOpen} />
            </>
        ) : (
            verb
        );
    }
    if (item.summary !== undefined) {
        return <EntityText text={item.summary} onOpen={item.onOpen} />;
    }
    if (
        item.status === "running" ||
        (item.resources?.length ?? 0) > 0 ||
        item.errorReason !== undefined ||
        item.resultLine !== undefined
    ) {
        return null;
    }
    return (
        <span className="tav-noDetail">
            <Trans>No details</Trans>
        </span>
    );
};

// One resource a call named: the composer's @-mention chip as a button when
// it can be opened, its bare title otherwise.
const ResourceItem = ({
    resource,
}: {
    readonly resource: ToolCallResource;
}): ReactNode => {
    switch (resource.type) {
        case "openable":
            return (
                <button
                    type="button"
                    className="tav-resource"
                    onClick={resource.onOpen}
                >
                    <ResourceMentionChip
                        resourceRef={{
                            kind: resource.kind,
                            id: resource.id,
                            title: resource.title,
                        }}
                    />
                </button>
            );
        case "inert":
            return (
                <Text as="span" size="xs" tone="faint">
                    {resource.title}
                </Text>
            );
        default:
            return exhaustSwitchCase(resource);
    }
};

// How many chips a listing shows before folding the rest behind "+N more".
const RESOURCE_PREVIEW = 3;

// The call's resources (F124). A long listing shows the first few; "+N more"
// unfolds the rest (view-ephemeral, like the trail's own disclosure).
const ResourceList = ({
    resources,
    onDownload,
}: {
    readonly resources: readonly ToolCallResource[];
    readonly onDownload?: () => void;
}): ReactNode => {
    const [expanded, setExpanded] = useState(false);
    if (resources.length === 0 && onDownload === undefined) return null;
    const shown = expanded ? resources : resources.slice(0, RESOURCE_PREVIEW);
    const restCount = resources.length - shown.length;
    return (
        <ul className="tav-resources">
            {shown.map((resource) => (
                <li key={resource.id}>
                    <ResourceItem resource={resource} />
                </li>
            ))}
            {restCount > 0 ? (
                <li>
                    <button
                        type="button"
                        className="tav-more"
                        onClick={() => setExpanded(true)}
                    >
                        <Text as="span" size="xs" tone="faint">
                            <Trans>+{restCount} more</Trans>
                        </Text>
                    </button>
                </li>
            ) : null}
            {onDownload !== undefined ? (
                <li>
                    <Link
                        tone="neutral"
                        href="#"
                        className="tav-link"
                        onClick={(event) => {
                            event.preventDefault();
                            onDownload();
                        }}
                    >
                        <Text as="span" size="xs">
                            <Trans>Download</Trans>
                        </Text>
                    </Link>
                </li>
            ) : null}
        </ul>
    );
};

// "12–15" for a range, "80" for a single page/section.
const rangeLabel = (range: ToolCallReadRange): string =>
    range.first === range.last
        ? String(range.first)
        : `${range.first}–${range.last}`;

// The unit word before the ranges ("s." / "p."·"pp." / "avsnitt"), counted
// over every page or section read.
const readSpanPrefix = (span: ToolCallReadSpan): MessageDescriptor => {
    const n = span.ranges.reduce((sum, r) => sum + r.last - r.first + 1, 0);
    switch (span.unit) {
        case "pages":
            return msg`${plural(n, { one: "p.", other: "pp." })}`;
        case "sections":
            return msg`${plural(n, { one: "section", other: "sections" })}`;
        default:
            return exhaustSwitchCase(span.unit);
    }
};

// "s. 12–15, 45–52, 80 i [källa]": the read parts, each range a link that
// opens the source there, then the source chip.
const ReadSpanLine = ({
    span,
    source,
}: {
    readonly span: ToolCallReadSpan;
    readonly source: ReactNode;
}): ReactNode => {
    const { i18n, t } = useLingui();
    return (
        <div className="tav-sentence">
            <Text as="span" size="xs" tone="faint">
                {i18n._(readSpanPrefix(span))}{" "}
                {span.ranges.map((range, i) => (
                    <span key={range.first}>
                        {i > 0 ? ", " : null}
                        <EntityText
                            text={rangeLabel(range)}
                            onOpen={range.onOpen}
                        />
                    </span>
                ))}{" "}
                {t({
                    context: "read locator, between the parts and the source",
                    message: "in",
                })}
            </Text>
            {source}
        </div>
    );
};

// One call's line — used both for a solo (ungrouped) row, where `verb`
// is shown inline, and for a child row inside an open group, where they're
// omitted (the group header already said the verb). It reads as a short
// agent message: the INTENT sentence ("Jag läste källan") with its target
// chips, then — when the call found something worth saying — a RESULT
// sentence ("Hittade 6 källor") carrying the chips instead.
const ToolCallLine = ({
    verb,
    item,
}: {
    readonly verb?: string;
    readonly item: ToolCallItem;
}): ReactNode => {
    const resources =
        item.resources !== undefined ? (
            <ResourceList
                resources={item.resources}
                onDownload={item.onDownload}
            />
        ) : null;
    const primary = primaryLine({ verb, item });
    return (
        <div className="tav-line">
            <StatusGlyph status={item.status} />
            <div className="tav-lineBody">
                <div className="tav-sentence">
                    {primary === null ? null : (
                        <Text
                            as="span"
                            size="xs"
                            tone={item.status === "failed" ? "danger" : "dim"}
                        >
                            {primary}
                        </Text>
                    )}
                    {item.resultLine === undefined &&
                    item.readSpan === undefined
                        ? resources
                        : null}
                </div>
                {item.readSpan !== undefined ? (
                    <ReadSpanLine span={item.readSpan} source={resources} />
                ) : null}
                {item.resultLine !== undefined ? (
                    <div className="tav-sentence">
                        <Text as="span" size="xs" tone="faint">
                            {item.resultLine}
                        </Text>
                        {resources}
                    </div>
                ) : null}
                {item.result !== undefined ? (
                    <Text
                        as="span"
                        size="xs"
                        tone="faint"
                        className="tav-result"
                    >
                        <EntityText
                            text={item.result.title}
                            onOpen={item.result.onOpen}
                        />
                        {item.result.meta !== undefined
                            ? ` · ${item.result.meta}`
                            : ""}
                    </Text>
                ) : null}
                {item.status === "failed" && item.errorReason !== undefined ? (
                    <Text
                        as="span"
                        size="xs"
                        tone="faint"
                        className="tav-error"
                    >
                        {item.errorReason}
                    </Text>
                ) : null}
                {item.status === "failed" && item.onReupload !== undefined ? (
                    <span className="tav-recovery">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={item.onReupload}
                        >
                            <Icon name="upload" size="sm" />
                            <Trans>Upload again</Trans>
                        </Button>
                    </span>
                ) : null}
            </div>
        </div>
    );
};

const ToolActivityGroupRow = ({
    group,
    open,
    showVerb,
    onToggle,
}: {
    readonly group: ToolActivityGroup;
    readonly open: boolean;
    readonly showVerb: boolean;
    readonly onToggle: () => void;
}): ReactNode => {
    // Rows read like the line above them, in plain words ("Read Chapter 4").
    const intent = (tense: Tense): string => groupVerb(group, tense);
    if (group.items.length === 1) {
        return (
            <li className="tav-row">
                <ToolCallLine
                    verb={
                        showVerb
                            ? intent(itemTense(group.items[0].status))
                            : undefined
                    }
                    item={group.items[0]}
                />
            </li>
        );
    }
    return (
        <li className="tav-groupRow">
            <button
                type="button"
                className="tav-groupHeader"
                aria-expanded={open}
                onClick={onToggle}
            >
                <Icon
                    name={open ? "chevron-down" : "chevron-right"}
                    size="xs"
                />
                <Text as="span" size="xs" tone="dim">
                    {intent(groupTense(group))}
                </Text>
                <Text as="span" size="xs" tone="faint" className="tav-count">
                    {group.items.length}
                </Text>
            </button>
            {open ? (
                <ul className="tav-children">
                    {group.items.map((item) => (
                        <li key={item.id} className="tav-childRow">
                            <ToolCallLine item={item} />
                        </li>
                    ))}
                </ul>
            ) : null}
        </li>
    );
};

// The whole trail's glyph: a failed step marks the line (it says what it
// means for you inside); a running one spins; otherwise done.
const trailGlyph = (groups: readonly ToolActivityGroup[]): TrailGlyph => {
    const items = groups.flatMap((group) => group.items);
    if (items.some((item) => item.status === "failed")) return "failed";
    if (items.some((item) => item.status === "running")) return "running";
    return "done";
};

// The one-line summary, localized.
const useTrailSummary = (groups: readonly ToolActivityGroup[]): string => {
    const { i18n } = useLingui();
    const summary = collapsedSummary(groups);
    return typeof summary === "string" ? summary : i18n._(summary);
};

// The steps under an open trail line (pen "Steg"), one row per group.
const ToolActivitySteps = ({
    groups,
    standalone = false,
}: {
    readonly groups: readonly ToolActivityGroup[];
    // Listed without this activity's own header line (inside an opened
    // merged line): every row names its step, since no header says it.
    readonly standalone?: boolean;
}): ReactNode => {
    const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(
        new Set(),
    );
    const toggleGroup = (id: string) =>
        setOpenGroups((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    return (
        <ul className="tav-groups">
            {groups.map((group) => (
                <ToolActivityGroupRow
                    key={group.id}
                    group={group}
                    open={openGroups.has(group.id)}
                    showVerb={standalone || rowVerbShown(groups)}
                    onToggle={() => toggleGroup(group.id)}
                />
            ))}
        </ul>
    );
};

const ToolActivityView = ({ model }: ToolActivityViewProps): ReactNode => {
    const { groups } = model;
    const summaryText = useTrailSummary(groups);
    if (groups.length === 0) return null;
    return (
        <TrailLine glyph={trailGlyph(groups)} text={summaryText}>
            <ToolActivitySteps groups={groups} />
        </TrailLine>
    );
};
